import { useState } from 'react'
import type { ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBolt,
  faBuilding,
  faCheck,
  faChevronDown,
  faEnvelope,
  faFileInvoice,
  faFlaskVial,
  faGear,
  faHelmetSafety,
  faIndustry,
  faLayerGroup,
  faShieldHalved,
  faTag,
  faTruckFast,
  faWind,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import {
  getProductPrimaryImageUrl,
  getStoreContactEmail,
  getStoreContactPhone,
  getStorefrontConfig,
  listStorefrontProducts,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontProduct } from '../lib/dropify'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { listDashboardNewsPosts } from '../lib/news'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildFaqSchema, DEFAULT_OG_IMAGE } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { BrandTrademark, SocialLinks } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { LpCrossLinks } from '../components/LpCrossLinks'

const WA_NUMBER = '6285704011028'
const PAGE_PATH = '/supplier-apd-pabrik'
const PAGE_NAME = 'Supplier APD Pabrik'
const PRODUCT_IMAGE_OPTIONS = { width: 640, format: 'webp', quality: 78 } as const
const HERO_IMAGE_OPTIONS = { width: 520, format: 'webp', quality: 84 } as const

type RiskKey = 'presisi' | 'minyak' | 'sayat' | 'kimia' | 'pernapasan'

type ProductMeta = {
  risk: RiskKey
  shortName: string
  standard: string
  useCase: string
}

// Claims (standards, AKD numbers, use cases) mirror docs/apd/produk-comet.md,
// which was transcribed from the official COMET catalog. Do not paraphrase
// standards; they must match the catalog exactly.
const PRODUCT_META: Record<string, ProductMeta> = {
  'COMET-CG805WT': {
    risk: 'presisi',
    shortName: 'CG 805 WT',
    standard: 'EN 388:2016 4131X',
    useCase: 'Perakitan, elektronik, otomotif, laboratorium',
  },
  'COMET-CG805BK': {
    risk: 'presisi',
    shortName: 'CG 805 BK',
    standard: 'EN 388:2016 4131X',
    useCase: 'Perakitan, elektronik, otomotif, laboratorium',
  },
  'COMET-CG801WT': {
    risk: 'presisi',
    shortName: 'CG 801 WT',
    standard: 'EN 388:2016 211XX',
    useCase: 'Inspeksi dan handling komponen sensitif',
  },
  'COMET-CG801BK': {
    risk: 'presisi',
    shortName: 'CG 801 BK',
    standard: 'EN 388:2016 211XX',
    useCase: 'Inspeksi dan handling komponen sensitif',
  },
  'COMET-CG803WT': {
    risk: 'presisi',
    shortName: 'CG 803 WT',
    standard: 'EN 388:2016 211XX',
    useCase: 'Perakitan komponen kecil dan perangkat presisi',
  },
  'COMET-CG813': {
    risk: 'presisi',
    shortName: 'CG 813 ESD',
    standard: 'EN 388:2016 211XX · EN 1149-1:2016',
    useCase: 'Semikonduktor dan perakitan elektronik',
  },
  'COMET-CG905GY': {
    risk: 'minyak',
    shortName: 'CG 905 GY',
    standard: 'EN 388:2016 3141X',
    useCase: 'Engineering, otomotif, maintenance berminyak',
  },
  'COMET-IMPACT': {
    risk: 'minyak',
    shortName: 'Impact Glove',
    standard: 'EN 388:2016 4544DP',
    useCase: 'Pekerjaan heavy duty, benturan, dan oli',
  },
  'COMET-CG835': {
    risk: 'sayat',
    shortName: 'CG 835',
    standard: 'EN 388:2016 4X43C',
    useCase: 'Handling kaca, fabrikasi logam, otomotif',
  },
  'COMET-CG835L': {
    risk: 'sayat',
    shortName: 'CG 835 Long',
    standard: 'EN 388:2016 4X43C',
    useCase: 'Perlindungan sayat hingga lengan bawah',
  },
  'COMET-CG835NC': {
    risk: 'sayat',
    shortName: 'CG 835 Non-Coat',
    standard: 'EN 388:2016 4543D',
    useCase: 'Handling kaca, keramik, dan tekstil',
  },
  'COMET-CG855': {
    risk: 'sayat',
    shortName: 'Aramid CG 855',
    standard: 'EN 388:2016 1341',
    useCase: 'Cutting, engineering, dan handling kaca',
  },
  'COMET-NF1513': {
    risk: 'kimia',
    shortName: 'NF 1513',
    standard: 'EN 388:2016 4001X · EN 374-1:2016 Type A',
    useCase: 'Chemical handling, oil refining, food processing',
  },
  'COMET-CG656': {
    risk: 'kimia',
    shortName: 'CG 656 PVC',
    standard: 'EN 388:2016 4113 · EN 374-1:2016',
    useCase: 'Petrokimia, cold storage, perikanan',
  },
  'COMET-THEO-3PLY': {
    risk: 'pernapasan',
    shortName: 'Theo 3 Ply',
    standard: 'CE · ISO 13485 · BFE 95% · AKD 21603220823',
    useCase: 'Masker harian untuk area kerja berdebu dan operasional pabrik',
  },
  'COMET-THEO-4D': {
    risk: 'pernapasan',
    shortName: 'Theo 4D',
    standard: 'CE · ISO 13485 · BFE 95% · AKD 20903220839',
    useCase: 'Masker 4 ply carbon. Periksa bentuk, filtrasi, izin edar, dan dokumen tipenya sebelum pengadaan.',
  },
  'COMET-THEO-KN95': {
    risk: 'pernapasan',
    shortName: 'Theo KN95',
    standard: 'CE · ISO 13485 · BFE 98% · AKD 20903220825',
    useCase: 'Filtrasi tinggi untuk area kerja berpartikel',
  },
  'COMET-THEO-4PLYC': {
    risk: 'pernapasan',
    shortName: 'Theo 4 Ply Carbon',
    standard: 'CE · ISO 13485',
    useCase: 'Lapisan karbon aktif untuk area berdebu dan berbau',
  },
}

const PRODUCT_ORDER = Object.keys(PRODUCT_META)

const RISK_GROUPS = [
  {
    key: 'presisi' as const,
    title: 'Presisi & ESD',
    description: 'Handling komponen, elektronik, inspeksi, dan perakitan yang menuntut sensitivitas tangan.',
    icon: faGear,
  },
  {
    key: 'minyak' as const,
    title: 'Minyak & Benturan',
    description: 'Grip dan perlindungan untuk maintenance, otomotif, alat berat, dan pekerjaan heavy duty.',
    icon: faIndustry,
  },
  {
    key: 'sayat' as const,
    title: 'Risiko Sayat',
    description: 'Untuk kaca, pelat logam, cutting, fabrikasi, dan material dengan tepian tajam.',
    icon: faLayerGroup,
  },
  {
    key: 'kimia' as const,
    title: 'Bahan Kimia',
    description: 'Perlindungan saat menangani chemical, caustic, oli, alkohol, dan proses petrokimia.',
    icon: faFlaskVial,
  },
  {
    key: 'pernapasan' as const,
    title: 'Pernapasan',
    description: 'Masker sekali pakai untuk kebutuhan operasional, area berpartikel, dan lingkungan berdebu.',
    icon: faWind,
  },
]

const WHY_US_ITEMS = [
  {
    icon: faTag,
    title: 'Quotation sesuai volume',
    desc: 'Quotation merinci SKU, jumlah, harga, dan ongkir agar tim pengadaan dapat membandingkan total biaya.',
  },
  {
    icon: faShieldHalved,
    title: 'Standar per SKU',
    desc: 'EN 388, EN 374, EN 1149 untuk sarung tangan, plus CE, ISO 13485, dan AKD untuk masker tertentu.',
  },
  {
    icon: faFileInvoice,
    title: 'PO dan invoice',
    desc: 'Sampaikan format Purchase Order dan kebutuhan invoice saat meminta quotation.',
  },
  {
    icon: faTruckFast,
    title: 'Kirim se-Indonesia',
    desc: 'Kirim alamat tujuan dan jumlah barang. Admin akan membandingkan layanan ekspedisi untuk pengiriman sekali beli atau suplai berkala.',
  },
]

const FIT_ITEMS = [
  'Tim purchasing atau pengadaan pabrik yang membutuhkan sarung tangan kerja dan masker dalam satu daftar kebutuhan.',
  'Tim K3 yang perlu membandingkan standar dan dokumen setiap SKU sebelum pengadaan.',
  'Workshop, bengkel, atau fabrikasi yang pekerjanya menangani material tajam, berminyak, atau kimia.',
  'Kontraktor dan proyek yang membutuhkan APD tangan dalam jumlah grosir untuk dikirim ke lokasi.',
  'Laboratorium atau lini elektronik yang membutuhkan sarung tangan presisi dan ESD.',
  'Pabrik yang perlu memeriksa harga, stok, dan waktu penyiapan untuk kebutuhan berkala.',
]

const PROCUREMENT_STEPS = [
  {
    n: '1',
    title: 'Jelaskan risiko kerja',
    desc: 'Sebutkan jenis pekerjaan, kondisi area, jumlah pekerja, dan frekuensi pemakaian.',
  },
  {
    n: '2',
    title: 'Kami cocokkan spesifikasi',
    desc: 'Kami menyaring bahan, coating, ukuran, dan standar yang relevan untuk diuji oleh tim K3 Anda.',
  },
  {
    n: '3',
    title: 'Terima penawaran resmi',
    desc: 'Penawaran tertulis dibuat berdasarkan SKU, volume, tujuan kirim, dan kebutuhan rutin perusahaan Anda.',
  },
  {
    n: '4',
    title: 'PO dan pengiriman',
    desc: 'Tim kami memproses pesanan untuk satu kali pengiriman atau jadwal suplai berkala ke lokasi Anda.',
  },
]

const COMPARISON_ROWS = [
  {
    risk: 'Presisi / elektronik',
    recommendation: 'CG 801, CG 803, CG 805, CG 813 ESD',
    material: 'Polyester 13 gauge, PU atau tanpa coating',
    standard: 'EN 388 · EN 1149 untuk seri ESD',
  },
  {
    risk: 'Area berminyak / heavy duty',
    recommendation: 'CG 905 GY, Impact Glove',
    material: 'Nitrile palm atau foam nitrile + TPR',
    standard: 'EN 388',
  },
  {
    risk: 'Sayatan / tepian tajam',
    recommendation: 'CG 835, CG 835 Long, CG 835 Non-Coat, CG 855',
    material: 'HPPE atau serat aramid',
    standard: 'EN 388',
  },
  {
    risk: 'Bahan kimia',
    recommendation: 'NF 1513, CG 656 PVC',
    material: 'Chemical nitrile atau PVC',
    standard: 'EN 374 · EN 388',
  },
  {
    risk: 'Debu / partikel',
    recommendation: 'Theo 3 Ply, 4D, KN95, 4 Ply Carbon',
    material: 'Melt-blown non-woven, opsi karbon aktif',
    standard: 'CE · ISO 13485 · AKD pada seri tertentu',
  },
]

const STANDARD_CARDS = [
  ['EN 388', 'Risiko mekanis', 'Abrasi, sayatan, sobek, tusukan, dan pada seri tertentu benturan.'],
  ['EN 374', 'Bahan kimia', 'Permeasi bahan kimia dan perlindungan terhadap mikroorganisme.'],
  ['EN 1149', 'Elektrostatik', 'Karakteristik pelepasan muatan statis untuk pekerjaan sensitif.'],
  ['CE · ISO · AKD', 'Masker', 'Klaim dan nomor registrasi berbeda pada setiap tipe masker COMET Theo.'],
]

const OBJECTIONS = [
  {
    worry: 'Bagaimana memeriksa klaim standar?',
    answer: 'Kode standar ditampilkan per SKU. Minta dokumen pendukung, lalu cocokkan dengan risk assessment dan SOP K3 sebelum memesan.',
  },
  {
    worry: 'Apakah pemesanan dapat memakai PO dan invoice?',
    answer: 'Bisa. Kami melayani pengadaan dengan Purchase Order dan menyiapkan invoice untuk administrasi tim purchasing Anda.',
  },
  {
    worry: 'Bagaimana jika tim belum menentukan SKU?',
    answer: 'Jelaskan risiko kerjanya terlebih dahulu. Kami menyaring SKU yang relevan, kemudian tim K3 Anda dapat menguji sample sebelum memesan dalam jumlah besar.',
  },
  {
    worry: 'Apakah Dropify menyediakan helm, sepatu safety, atau body harness?',
    answer: 'Fokus kami adalah APD tangan dan pernapasan. Untuk jenis APD lain, admin akan menyampaikan dengan jelas jika barang tersebut tidak tersedia di Dropify.',
  },
  {
    worry: 'Data apa yang tersedia untuk membandingkan pemasok?',
    answer: 'Minta quotation tertulis terlebih dahulu agar tim Anda dapat membandingkan produk, jumlah, dan biaya sebelum mengajukannya untuk persetujuan.',
  },
]

const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'APD apa saja yang tersedia di halaman ini?',
    a: 'Halaman ini membahas APD tangan dan pernapasan dari COMET: sarung tangan presisi, ESD, lapis nitrile, anti-sayat, chemical, impact, serta masker Theo 3 Ply, 4D, KN95, dan 4 Ply Carbon. Katalog ini tidak mencakup helm, sepatu safety, atau body harness.',
  },
  {
    q: 'Bagaimana memilih sarung tangan yang sesuai pekerjaan?',
    a: 'Kirim jenis pekerjaan, material yang ditangani, kondisi kering atau berminyak, paparan bahan kimia, serta risiko sayat atau benturan. Admin akan menyaring SKU dan standar yang relevan untuk diuji oleh tim K3 Anda.',
  },
  {
    q: 'Standar keselamatan apa yang tercantum?',
    a: 'Standarnya berbeda per SKU. EN 388 berlaku pada seri untuk risiko mekanis, EN 374 pada seri chemical, dan EN 1149 pada seri ESD. CE, ISO 13485, data BFE, serta nomor AKD hanya berlaku pada masker tertentu. Minta dokumen untuk SKU yang akan dimasukkan ke PO.',
  },
  {
    q: 'Bisa minta penawaran resmi dan beli pakai PO?',
    a: 'Bisa. Sebutkan SKU atau jenis pekerjaan, volume, dan lokasi kirim. Admin akan membuat quotation tertulis. Kebutuhan Purchase Order dan invoice dapat disampaikan pada pesan yang sama.',
  },
  {
    q: 'Bisa suplai rutin untuk pabrik?',
    a: 'Bisa. Kirim jumlah pekerja, tingkat pemakaian, dan jadwal kebutuhan. Admin akan memeriksa ketersediaan serta lead time setiap SKU saat menyiapkan penawaran.',
  },
  {
    q: 'Berapa harga dan minimum order produk COMET?',
    a: 'MOQ sarung tangan umumnya mulai 25 lusin. Untuk lini COMET, ketentuan dapat berbeda per SKU. Kirim seri dan jumlah yang dibutuhkan agar admin dapat mengonfirmasi MOQ melalui quotation.',
    link: { href: '/minimum-order', label: 'Lihat detail minimum order per kategori →' },
  },
  {
    q: 'Bisa dikirim ke luar Jawa?',
    a: 'Bisa. Kirim kota tujuan dan jumlah barang. Admin akan mencantumkan pilihan pengiriman serta ongkir dalam penawaran.',
  },
]

export const Route = createFileRoute('/supplier-apd-pabrik')({
  loader: async () => {
    const [config, productResponse, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontProducts({ search: 'COMET', per_page: 100 }).catch(() => null),
      listDashboardNewsPosts().catch(() => []),
    ])

    const products = (productResponse?.items ?? [])
      .filter((product) => product.sku in PRODUCT_META)
      .sort((a, b) => PRODUCT_ORDER.indexOf(a.sku) - PRODUCT_ORDER.indexOf(b.sku))

    return { config, products, newsPosts }
  },
  head: () => {
    const title = brandTitle('Supplier APD Pabrik - Sarung Tangan & Masker Industri')
    const description =
      'Supplier APD tangan dan pernapasan untuk pabrik: sarung tangan COMET EN 388, EN 374, ESD, anti-sayat, dan masker industri. Bisa PO dan penawaran resmi.'

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS)),
        },
      ],
    }
  },
  component: SupplierApdPage,
})

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

// Amber highlighter swipe behind the hook keyword — the page's signature.
function Mark({ children }: { children: ReactNode }) {
  return (
    <span className="relative whitespace-nowrap">
      <span className="relative z-10">{children}</span>
      <span aria-hidden className="absolute -inset-x-1 bottom-0.5 z-0 h-[0.5em] -rotate-1 rounded-sm bg-amber-400/80" />
    </span>
  )
}

function WhatsAppButton({
  message,
  label,
  itemName,
  placement,
  className,
}: {
  message: string
  label: string
  itemName?: string
  placement?: string
  className?: string
}) {
  return (
    <a
      href={waLink(message)}
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement ?? (itemName ? 'product_card' : 'body_primary'),
          lp_category: PAGE_NAME,
          ...(itemName
            ? { lead_item_name: itemName, items: [{ item_id: itemName, item_name: itemName, item_category: PAGE_NAME }] }
            : {}),
        })}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
      {label}
    </a>
  )
}

function ProductImage({
  product,
  eager = false,
  className = '',
}: {
  product: StorefrontProduct
  eager?: boolean
  className?: string
}) {
  const sourceUrl = getProductPrimaryImageUrl(product)
  if (!sourceUrl) {
    return (
      <div className={`flex items-center justify-center bg-slate-50 text-center ${className}`}>
        <span className="px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">
          Foto produk belum tersedia
        </span>
      </div>
    )
  }

  return (
    <img
      src={toExternalImage(sourceUrl, eager ? HERO_IMAGE_OPTIONS : PRODUCT_IMAGE_OPTIONS)}
      alt={product.name}
      width="640"
      height="640"
      loading={eager ? 'eager' : 'lazy'}
      fetchPriority={eager ? 'high' : 'auto'}
      decoding="async"
      className={className}
    />
  )
}

// Four real COMET product photos fanned out as the hero visual, in place of
// the single hero image the sibling LPs use.
function HeroProductStack({ products }: { products: StorefrontProduct[] }) {
  const featuredSkus = ['COMET-IMPACT', 'COMET-NF1513', 'COMET-CG835', 'COMET-THEO-KN95']
  const featured = featuredSkus
    .map((sku) => products.find((product) => product.sku === sku))
    .filter((product): product is StorefrontProduct => Boolean(product))

  if (featured.length === 0) return null

  return (
    <div className="relative min-h-[430px] sm:min-h-[500px]">
      <div className="absolute inset-4 rounded-3xl bg-white/5 ring-1 ring-white/10 shadow-2xl" />
      {featured.map((product, index) => {
        const positions = [
          'left-[4%] top-[7%] w-[48%] -rotate-3',
          'right-[1%] top-[2%] w-[47%] rotate-3',
          'bottom-[3%] left-[7%] w-[43%] rotate-2',
          'bottom-[2%] right-[2%] w-[46%] -rotate-2',
        ]

        return (
          <a
            key={product.id}
            href={`/produk/${product.slug || product.id}`}
            className={`absolute ${positions[index]} group rounded-2xl bg-white p-2 shadow-xl ring-1 ring-slate-200 transition-transform duration-300 hover:z-20 hover:scale-[1.03]`}
          >
            <ProductImage
              product={product}
              eager={index < 2}
              className="aspect-square w-full rounded-xl object-contain"
            />
            <span className="absolute bottom-3 left-3 rounded-full bg-blue-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow">
              {PRODUCT_META[product.sku].shortName}
            </span>
          </a>
        )
      })}
      <div className="absolute -bottom-1 left-1/2 z-30 -translate-x-1/2 whitespace-nowrap rounded-full bg-amber-400 px-5 py-2.5 text-xs font-bold text-slate-900 shadow-lg">
        Pilihan SKU COMET
      </div>
    </div>
  )
}

function SupplierApdPage() {
  const { config, products, newsPosts } = Route.useLoaderData()
  const [activeRisk, setActiveRisk] = useState<RiskKey | 'semua'>('semua')

  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)
  // Counts derive from the products the storefront actually returned, so a
  // deactivated SKU or partial API response never leaves the chips overstating.
  const riskCounts: Partial<Record<RiskKey, number>> = {}
  for (const product of products) {
    const risk = PRODUCT_META[product.sku]?.risk
    if (risk) riskCounts[risk] = (riskCounts[risk] ?? 0) + 1
  }
  const visibleProducts =
    activeRisk === 'semua'
      ? products
      : products.filter((product) => PRODUCT_META[product.sku]?.risk === activeRisk)

  const heroMessage =
    'Halo Dropify, saya mencari APD untuk pabrik. Risiko kerja kami: ___. Kebutuhan: ___. Standar wajib: ___. Mohon pilihkan sarung tangan atau masker COMET dan siapkan quotation.'
  const softMessage =
    'Halo Dropify, saya sedang mencari pemasok APD untuk pabrik. Mohon jelaskan jenis produk, standar, MOQ, dan dokumen yang tersedia.'
  const consultationMessage =
    'Halo Dropify, saya ingin memeriksa pilihan APD COMET. Jenis pekerjaan dan risiko di tempat kami: ___. Standar wajib: ___.'
  const quotationMessage =
    'Halo Dropify, saya ingin meminta quotation APD COMET untuk kebutuhan pabrik. Produk atau risiko kerja: ___. Jumlah: ___. Kota tujuan: ___. Mohon periksa MOQ, stok, harga, dan waktu penyiapan.'

  return (
    <div className="min-h-screen bg-white">
      {/* Urgency strip */}
      <div className="bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold px-4 py-2">
        <FontAwesomeIcon icon={faBolt} className="w-3.5 h-3.5 mr-1.5" />
        Pengadaan APD tangan & pernapasan untuk pabrik. Bisa PO dan invoice resmi.
      </div>

      {/* Minimal header — logo + single CTA, no nav links to avoid click leaks */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" aria-label="Dropify">
            <img src="/logo80.webp" srcSet="/logo40.webp 40w, /logo80.webp 80w" sizes="40px" alt="Dropify" width="40" height="40" className="h-10 w-10 object-contain" />
            <span className="text-lg font-black tracking-tight text-slate-900">Dropify</span>
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Minta penawaran"
            placement="header"
            className="inline-flex items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-4 py-2.5 text-sm font-bold text-white transition-colors"
          />
        </div>
      </header>

      {/* Hero — hook + dual CTA */}
      <section className="bg-[#0B1120] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
              <FontAwesomeIcon icon={faHelmetSafety} className="h-3 w-3" />
              COMET · APD tangan & pernapasan
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]">
              Pilih APD COMET <Mark>berdasarkan risiko kerja.</Mark>
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
              Sarung tangan industri dan masker COMET untuk lini produksi, maintenance, laboratorium, workshop, dan proyek. Utamakan risiko, standar, dan lingkungan kerja saat memilih produk. Pertimbangkan harga setelah kecocokan produknya jelas.
            </p>

            {products.length > 0 && (
              <div className="mt-6 inline-flex items-baseline gap-2.5 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-300">Tersedia</span>
                <span className="text-2xl md:text-3xl font-black leading-none text-white">{products.length} SKU</span>
                <span className="text-sm font-medium text-slate-300">dalam 5 kelompok risiko kerja</span>
              </div>
            )}

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <WhatsAppButton
                message={heroMessage}
                label="Minta quotation"
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
              />
              <a
                href={waLink(softMessage)}
                {...whatsAppCtaProps('landing_page', { cta_placement: 'hero_secondary', lp_category: PAGE_NAME })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Periksa jenis dan standar →
              </a>
            </div>
            <p className="mt-3 text-sm text-slate-400">Minta dokumen standar untuk SKU yang akan diperiksa sebelum menerbitkan PO.</p>
          </div>

          <HeroProductStack products={products} />
        </div>
      </section>

      <AdNewsProofSection posts={newsPosts} />

      <WhatsAppChannelSection showSocksPromo={false} />

      {/* Who it's for — let the procurement visitor self-identify */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Kebutuhan pengadaan yang kami layani</h2>
            <p className="mt-2 text-base text-slate-500">Dropify melayani pengadaan APD tangan dan pernapasan untuk pabrik. Periksa apakah kebutuhan tim Anda termasuk dalam cakupan berikut.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {FIT_ITEMS.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                  <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
                </span>
                <p className="text-base leading-relaxed text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Data yang tersedia untuk tim pengadaan</h2>
            <p className="mt-2 text-base text-slate-500">Quotation, standar per SKU, dokumen administrasi, dan pilihan pengiriman dibahas sebelum pemesanan.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {WHY_US_ITEMS.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600">
                  <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-white" />
                </div>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk selector — the page's unique merchandising angle */}
      <section className="py-14 bg-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Saring produk berdasarkan risiko kerja</h2>
            <p className="mt-2 text-base text-slate-500">Setiap pekerjaan memiliki risiko yang berbeda. Gunakan peta ini sebagai penyaring awal, lalu minta tim K3 memvalidasi produk sebelum pengadaan besar.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {RISK_GROUPS.map((group) => (
              <button
                key={group.key}
                type="button"
                onClick={() => {
                  setActiveRisk(group.key)
                  document.getElementById('produk-apd')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className="group flex flex-col rounded-2xl border border-slate-100 bg-white p-5 text-left transition-all hover:border-blue-200 hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600">
                    <FontAwesomeIcon icon={group.icon} className="h-5 w-5 text-white" />
                  </span>
                  {(riskCounts[group.key] ?? 0) > 0 && (
                    <span className="text-xs font-semibold text-slate-400">{riskCounts[group.key]} SKU</span>
                  )}
                </div>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{group.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{group.description}</p>
                <span className="mt-auto flex items-center gap-1.5 pt-4 text-sm font-semibold text-blue-600 group-hover:text-blue-700">
                  Lihat produk
                  <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product catalog with risk filter */}
      <section id="produk-apd" className="scroll-mt-4 py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pilihan APD COMET untuk pabrik</h2>
            <p className="mt-2 text-base text-slate-500">Bandingkan jenis lapisan, fungsi, dan standar yang tercantum untuk setiap SKU sebelum menyusun kebutuhan pengadaan.</p>
          </div>

          {products.length > 0 && (
          <div className="mb-8 flex gap-2 overflow-x-auto pb-2">
            <button
              type="button"
              onClick={() => setActiveRisk('semua')}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                activeRisk === 'semua'
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'
              }`}
            >
              Semua ({products.length})
            </button>
            {RISK_GROUPS.map((group) => (
              <button
                key={group.key}
                type="button"
                onClick={() => setActiveRisk(group.key)}
                className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                  activeRisk === group.key
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'
                }`}
              >
                {group.title} ({riskCounts[group.key] ?? 0})
              </button>
            ))}
          </div>
          )}

          {visibleProducts.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visibleProducts.map((product) => {
                const meta = PRODUCT_META[product.sku]
                return (
                  <article
                    key={product.id}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-lg"
                  >
                    <a href={`/produk/${product.slug || product.id}`} className="relative block overflow-hidden bg-slate-50">
                      <ProductImage
                        product={product}
                        className="aspect-square w-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute left-3 top-3 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow">
                        {RISK_GROUPS.find((group) => group.key === meta.risk)?.title}
                      </span>
                    </a>
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="text-base font-black tracking-tight text-slate-900">{meta.shortName}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{meta.useCase}</p>
                      <p className="mt-3 border-t border-slate-100 pt-3 text-xs font-semibold text-slate-600">{meta.standard}</p>
                      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                        <WhatsAppButton
                          message={`Halo Dropify, saya ingin memeriksa ${meta.shortName} (${product.sku}) untuk kebutuhan pabrik. Jumlah: ___. Kota tujuan: ___. Mohon periksa MOQ, stok, harga, dan dokumen produknya.`}
                          label="Periksa stok dan dokumen"
                          itemName={product.sku}
                          className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-green-700 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-800"
                        />
                        <a href={`/produk/${product.slug || product.id}`} className="text-sm font-semibold text-blue-600 hover:text-blue-700">
                          Lihat detail →
                        </a>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-8 text-center">
              <p className="text-lg font-bold text-slate-900">Produk belum dapat ditampilkan.</p>
              <p className="mt-2 text-sm text-slate-500">Hubungi admin untuk meminta daftar SKU dan spesifikasi yang tersedia.</p>
              <WhatsAppButton
                message={quotationMessage}
                label="Minta katalog melalui WhatsApp"
                placement="catalog_empty"
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-5 py-3 text-sm font-bold text-white transition-colors"
              />
            </div>
          )}
        </div>
      </section>

      {/* Selection matrix */}
      <section className="py-14 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Cocokkan risiko, material, dan standar</h2>
            <p className="mt-2 text-base text-slate-500">Matriks ini merupakan panduan awal dari spesifikasi produk. Keputusan akhir tetap mengikuti risk assessment dan SOP K3 perusahaan Anda.</p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-bold">Risiko kerja</th>
                  <th className="px-4 py-3 font-bold">Rekomendasi seri</th>
                  <th className="px-4 py-3 font-bold">Material utama</th>
                  <th className="px-4 py-3 font-bold">Standar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.risk}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{row.risk}</td>
                    <td className="px-4 py-3 text-slate-600">{row.recommendation}</td>
                    <td className="px-4 py-3 text-slate-600">{row.material}</td>
                    <td className="px-4 py-3 text-slate-600">{row.standard}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-2xl bg-blue-50 border border-blue-100 px-6 py-5">
            <p className="text-base font-semibold text-slate-700">Kirim jenis pekerjaan dan risiko utamanya agar admin dapat menyaring SKU untuk diperiksa tim K3.</p>
            <WhatsAppButton
              message={consultationMessage}
              label="Periksa pilihan APD"
              placement="consultation"
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-5 py-3 text-sm font-bold text-white transition-colors"
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Dari risiko kerja sampai barang tiba</h2>
            <p className="mt-2 text-base text-slate-500">Empat tahap dari penyampaian kebutuhan hingga pesanan diterima di pabrik.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PROCUREMENT_STEPS.map((step) => (
              <div key={step.n} className="rounded-2xl border border-slate-100 bg-white p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400 text-sm font-black text-slate-900">{step.n}</span>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards explainer */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Arti kode standar pada setiap SKU</h2>
            <p className="mt-2 text-base text-slate-500">Setiap kode menunjukkan jenis pengujian yang berbeda. Standar harus dibaca per SKU dan dicocokkan dengan bahaya di tempat kerja Anda.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STANDARD_CARDS.map(([code, title, description]) => (
              <div key={code} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <p className="text-2xl font-black tracking-tight text-blue-600">{code}</p>
                <p className="mt-3 text-base font-black tracking-tight text-slate-900">{title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Objection handling */}
      <section className="py-14 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Hal yang perlu dipastikan sebelum PO</h2>
            <p className="mt-2 text-base text-slate-500">Periksa cakupan produk, pemilihan SKU, sample, dokumen, PO, dan kebutuhan rutin.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {OBJECTIONS.map((item) => (
              <div key={item.worry} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <p className="text-base font-bold text-slate-900">{item.worry}</p>
                <div className="mt-2 flex gap-2.5">
                  <FontAwesomeIcon icon={faCheck} className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                  <p className="text-base leading-relaxed text-slate-600">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl bg-blue-50 border border-blue-100 px-6 py-6 text-center sm:text-left">
            <p className="text-base font-semibold text-slate-700">Kirim persyaratan pengadaan kepada admin sebelum menerbitkan PO.</p>
            <WhatsAppButton
              message={softMessage}
              label="Tanyakan kepada admin"
              placement="objection_section"
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-5 py-3 text-sm font-bold text-white transition-colors"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <LpFaqSection faqItems={FAQ_ITEMS} />

      {/* SEO prose — works the target search terms in naturally, scoped
          honestly to hand & respiratory PPE only. */}
      <section className="bg-white py-14 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-black tracking-tight text-slate-900">Supplier APD pabrik untuk perlindungan tangan dan pernapasan</h2>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-600">
            <p>
              Dropify menyuplai <strong className="font-semibold text-slate-800">APD K3</strong> untuk perlindungan tangan dan pernapasan. Helm, sepatu safety, dan body harness berada di luar lini yang dilayani halaman ini. Kirim risiko kerja serta standar wajib agar admin dapat memeriksa sarung tangan atau masker yang relevan.
            </p>
            <p>
              Lini <strong className="font-semibold text-slate-800">sarung tangan safety</strong> COMET mencakup seri presisi dan ESD untuk elektronik, lapis nitrile untuk area berminyak, anti-sayat HPPE dan aramid untuk kaca serta fabrikasi logam, serta chemical nitrile dan PVC untuk bahan kimia. Standar dicantumkan per SKU: EN 388 untuk risiko mekanis, EN 374 untuk seri chemical, dan EN 1149 untuk seri ESD. Lini <strong className="font-semibold text-slate-800">masker industri</strong> COMET Theo mencakup 3 ply, KN95, dan 4 ply carbon, dengan CE, ISO 13485, serta nomor AKD pada seri tertentu.
            </p>
            <p>
              Pemilihan dimulai dari risiko kerja. Kirim jenis pekerjaan, material yang ditangani, dan kondisi area. Admin akan menyaring SKU untuk diuji tim K3 Anda serta menyiapkan quotation. Kebutuhan PO dan invoice dapat disampaikan saat meminta penawaran.
            </p>
            <p>
              Untuk pengadaan sarung tangan kerja dalam jumlah grosir dan kebutuhan berkala, buka halaman{' '}
              <a href="/sarung-tangan-pabrik" className="font-semibold text-blue-600 hover:text-blue-700">
                sarung tangan pabrik
              </a>
              . Daftar masker tersedia pada halaman{' '}
              <a href="/kategori/masker" className="font-semibold text-blue-600 hover:text-blue-700">
                kategori masker
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0B1120] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">Kirim risiko kerja agar admin dapat menyaring SKU</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
            Sertakan jumlah pekerja, jenis pekerjaan, standar wajib, lokasi pengiriman, dan kebutuhan per periode agar admin dapat menyusun quotation yang sesuai.
          </p>
          <div className="mt-7 flex justify-center">
            <WhatsAppButton
              message={quotationMessage}
              label="Minta quotation melalui WhatsApp"
              placement="closing"
              className="inline-flex items-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
            />
          </div>
          <p className="mt-4 text-sm text-amber-300/90">
            <FontAwesomeIcon icon={faBolt} className="w-3 h-3 mr-1" />
            Dokumen standar perlu diperiksa untuk SKU yang dipilih.
          </p>

          {/* Legitimacy: accepted payment methods, reassures a first-time procurement buyer */}
          <div className="mt-9 flex flex-col items-center gap-3 border-t border-white/10 pt-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Menerima pembayaran</p>
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/bca.svg" alt="BCA" width="72" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/mandiri.png" alt="Mandiri" width="80" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with legitimacy + contact (no nav links — keeps the funnel tight) */}
      {/* Cross-links to sibling landing pages */}
      <LpCrossLinks currentPath="/supplier-apd-pabrik" />

      <footer className="border-t border-slate-100 bg-white py-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="inline-flex items-center gap-2 text-sm font-black tracking-tight text-slate-900">
            <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-blue-600" />
            PT Dropify Distribusi Indonesia
          </p>
          <p className="mt-1.5 text-xs text-slate-500">Pengadaan grosir melalui WhatsApp</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
            <a
              href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`}
              {...whatsAppCtaProps('landing_page', { cta_placement: 'footer_phone', lp_category: PAGE_NAME })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-3.5 w-3.5 text-green-600" />
              {contactPhone}
            </a>
            <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-1.5 hover:text-blue-700 transition-colors">
              <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 text-blue-600" />
              {contactEmail}
            </a>
          </div>
          <div className="mt-5 flex justify-center">
            <SocialLinks variant="light" />
          </div>
          <p className="mt-5 text-xs text-slate-400">© Dropify. Semua hak cipta dilindungi.</p>
          <BrandTrademark className="mt-1 text-xs text-slate-400" />
        </div>
      </footer>
    </div>
  )
}

function LpFaqSection({ faqItems }: { faqItems: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-slate-50 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan seputar pengadaan APD</h2>
          <p className="mt-2 text-base text-slate-500">Kirim pertanyaan yang belum terjawab kepada admin melalui WhatsApp.</p>
        </div>
        <div className="space-y-2">
          {faqItems.map((item, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => {
                  if (openIndex !== i) track('faq_open', { faq_question: item.q, faq_location: 'supplier_apd' })
                  setOpenIndex(openIndex === i ? null : i)
                }}
                aria-expanded={openIndex === i}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-semibold text-slate-800 transition-colors hover:text-blue-600"
              >
                <span>{item.q}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}
                />
              </button>
              {openIndex === i && (
                <div className="border-t border-slate-100 px-5 pb-4 pt-3 text-base leading-relaxed text-slate-600">
                  {item.a}
                  {item.link && (
                    <a href={item.link.href} className="mt-2 block font-semibold text-blue-600 hover:text-blue-700">
                      {item.link.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
